import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, CreditCard } from "lucide-react";

import { BrandMark } from "@/components/BrandMark";
import { OrderSummaryView } from "@/components/orders/OrderSummaryView";
import { SignFlow } from "@/components/orders/SignFlow";
import { api } from "@/lib/api-client";
import { aed, PAYMENT_KIND_LABEL, type PublicOrderView } from "@/lib/orders";

/** `/o/:token` — the customer's own page: review the order, get a code, sign. */
export default function SignPage() {
  const { token = "" } = useParams();
  const [params] = useSearchParams();
  const justPaid = params.get("paid");
  const [view, setView] = useState<PublicOrderView | null | undefined>(undefined);
  const [signedUrl, setSignedUrl] = useState<string | null>(null);

  useEffect(() => {
    api
      .publicOrder(token)
      .then(setView)
      .catch(() => setView(null));
  }, [token]);

  const pdfUrl = api.publicOrderPdfUrl(token);

  return (
    <div className="app-surface min-h-screen px-4 py-8">
      <div className="mx-auto max-w-md">
        <div className="mb-6 flex items-center gap-3">
          <BrandMark className="h-10 w-10" />
          <div className="leading-tight">
            <div className="brand-wordmark text-[0.7rem] text-paper">VERSUS</div>
            <div className="text-[0.55rem] uppercase tracking-[0.3em] text-ice/60">Concept · order</div>
          </div>
        </div>

        {view === undefined && <p className="text-sm text-ice/60">Loading…</p>}
        {view === null && (
          <div className="glass-panel-strong p-5 text-sm text-paper/90">This link is not valid. Please contact Versus Concept.</div>
        )}

        {view && (
          <>
            <div className="brand-kicker">Order {view.number}</div>
            <h1 className="font-serif text-2xl text-paper">{view.customerName}</h1>
            <p className="mb-5 text-sm text-ice/70">{view.siteAddress}</p>

            {signedUrl || view.status === "signed" ? (
              <div className="glass-panel-strong p-5 text-center">
                <CheckCircle2 className="mx-auto h-10 w-10 text-green" />
                <p className="mt-3 font-serif text-xl text-paper">Thank you — order {view.number} is signed.</p>
                <a href={signedUrl ? `${api.publicOrderPdfUrl(token)}?signed=1` : pdfUrl} target="_blank" rel="noopener noreferrer" className="mt-3 inline-block text-sm text-pink underline underline-offset-4">
                  Download the signed PDF
                </a>
              </div>
            ) : null}

            {(signedUrl || view.status === "signed") && (view.payments.length > 0 || justPaid) ? (
              <div className="glass-panel-strong mt-4 p-5">
                <div className="brand-kicker">Payments</div>
                {justPaid && <p className="mt-2 text-sm text-green">Thank you — your {justPaid === "deposit" ? "deposit" : "payment"} is being confirmed.</p>}
                <div className="mt-2 flex justify-between text-sm text-ice/80"><span>Paid</span><b className="num text-paper">AED {aed(view.paymentSummary.paid)}</b></div>
                <div className="flex justify-between text-sm text-ice/80"><span>Outstanding</span><b className="num text-goldbright">AED {aed(view.paymentSummary.outstanding)}</b></div>
                {view.payments.filter((p) => p.status === "pending" && p.url).map((p) => (
                  <a key={p.id} href={p.url!} className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full bg-magenta px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.16em] text-paper hover:brightness-110">
                    <CreditCard className="h-4 w-4" /> Pay {PAYMENT_KIND_LABEL[p.kind].toLowerCase()} · AED {aed(p.amount)}
                  </a>
                ))}
                {view.payments.filter((p) => p.status === "paid").map((p) => (
                  <div key={p.id} className="mt-2 flex justify-between text-xs text-ice/70"><span><CheckCircle2 className="mr-1 inline h-3.5 w-3.5 text-green" />{PAYMENT_KIND_LABEL[p.kind]} received{p.paidAt ? ` on ${new Date(p.paidAt).toLocaleDateString("en-GB")}` : ""}</span><b className="num text-paper">AED {aed(p.amount)}</b></div>
                ))}
                <p className="mt-3 text-[0.65rem] text-ice/45">Card payments are processed by Stripe. Bank transfer details are printed on your order.</p>
              </div>
            ) : null}

            {signedUrl || view.status === "signed" ? (
              null
            ) : view.status === "cancelled" ? (
              <div className="glass-panel-strong p-5 text-sm text-paper/90">This order was cancelled. Please contact Versus Concept for an updated one.</div>
            ) : (
              <>
                <OrderSummaryView view={view} pdfUrl={pdfUrl} />
                <div className="mt-6">
                  {view.canSign ? (
                    <SignFlow
                      defaultName={view.customerName}
                      hasEmail={view.hasEmail}
                      requestOtp={(channel) => api.publicOrderOtp(token, channel)}
                      sign={(dto) => api.publicOrderSign(token, dto)}
                      onSigned={setSignedUrl}
                    />
                  ) : (
                    <div className="glass-panel-strong p-5 text-sm text-paper/90">This order is being prepared. You will be able to sign it once Versus Concept sends it.</div>
                  )}
                </div>
              </>
            )}
            <p className="mt-8 text-center text-[0.65rem] text-ice/40">Electronic signature recorded by VERSUS Surface ID · UAE Federal Decree-Law 46/2021</p>
          </>
        )}
      </div>
    </div>
  );
}
